import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Trash, Plus, ArrowRight, Star, Copy, Eye, EyeSlash, ArrowUp, ArrowDown, DotsSixVertical, TreeStructure } from "@phosphor-icons/react";
import { FlexiblePriceWizard } from "@/components/FlexiblePriceWizard";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * Iter117 — Editor Prezzo Flessibile con logica condizionale (bivi).
 *
 * Rappresenta l'albero `pricing_logic` come una lista di "nodi domanda".
 * Ogni domanda contiene N risposte con:
 *   - price_modifier {value, type: "pct" | "eur"}
 *   - next_question_id (dropdown fra le altre domande dell'albero)
 *
 * L'editor è volutamente semplice: layout a cascata / cards impilate,
 * con un indicatore di "domanda iniziale" (`is_root`) per marcare da
 * dove parte il flow lato marketplace.
 */
export function PricingLogicEditor({ value, onChange, servicesForClone = [], currentServiceId = null }) {
  const questions = Array.isArray(value) ? value : [];
  const [cloneFromId, setCloneFromId] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBasePrice, setPreviewBasePrice] = useState(100);
  const [previewAnswers, setPreviewAnswers] = useState([]);
  const [previewDelta, setPreviewDelta] = useState(0);
  const [treeOpen, setTreeOpen] = useState(false);

  // Iter122: sensor per drag & drop (attivazione dopo 5px per non interferire con i click)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const sorted = [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const oldIdx = sorted.findIndex(q => q.id === active.id);
    const newIdx = sorted.findIndex(q => q.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(sorted, oldIdx, newIdx);
    // Riassegna order in modo sequenziale
    const orderMap = new Map(reordered.map((q, i) => [q.id, i]));
    onChange(questions.map(q => ({ ...q, order: orderMap.get(q.id) ?? q.order ?? 0 })));
  };

  const genId = (prefix) => `${prefix}_${Date.now()}_${Math.floor(Math.random() * 10000)}`;

  const addQuestion = () => {
    const isFirst = questions.length === 0;
    const maxOrder = questions.reduce((m, q) => Math.max(m, q.order ?? 0), -1);
    const q = {
      id: genId("q"),
      question: "",
      is_root: isFirst,
      order: maxOrder + 1,
      parent_question_id: null,   // Iter121: bivio condizionale (padre)
      parent_answer_id: null,     // Iter121: risposta padre che rende visibile la domanda
      answers: [
        { id: genId("a"), label: "Sì", price_modifier: { type: "pct", value: 0 }, next_question_id: null },
        { id: genId("a"), label: "No", price_modifier: { type: "pct", value: 0 }, next_question_id: null },
      ],
    };
    onChange([...questions, q]);
  };

  // Iter121: sposta una domanda in su/giù (swap del campo `order`).
  const moveQuestion = (qid, direction) => {
    const sorted = [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const idx = sorted.findIndex(q => q.id === qid);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[swapIdx];
    const aOrder = a.order ?? idx;
    const bOrder = b.order ?? swapIdx;
    onChange(questions.map(q => {
      if (q.id === a.id) return { ...q, order: bOrder };
      if (q.id === b.id) return { ...q, order: aOrder };
      return q;
    }));
  };

  // Iter121: quando il padre cambia (o viene rimosso), sincronizza
  // `next_question_id` sulla risposta padre → figlio, per far coincidere
  // il bivio conditional con il flusso navigato lato marketplace.
  const setParent = (qid, parentQid, parentAid) => {
    onChange(questions.map(q => {
      // Rimuovi eventuali vecchi puntatori da altre domande a `qid`.
      const cleanedAnswers = (q.answers || []).map(a =>
        a.next_question_id === qid && !(q.id === parentQid && a.id === parentAid)
          ? { ...a, next_question_id: null }
          : a,
      );
      if (q.id === qid) {
        // La domanda figlio salva parent_* e viene marcata NON-root.
        return {
          ...q,
          parent_question_id: parentQid || null,
          parent_answer_id: parentAid || null,
          is_root: parentQid ? false : q.is_root,
          answers: cleanedAnswers,
        };
      }
      if (q.id === parentQid) {
        // Sul padre, imposta next_question_id sulla risposta scelta.
        return {
          ...q,
          answers: cleanedAnswers.map(a =>
            a.id === parentAid ? { ...a, next_question_id: qid } : a,
          ),
        };
      }
      return { ...q, answers: cleanedAnswers };
    }));
  };

  const patchQuestion = (qid, patch) => {
    onChange(questions.map(q => (q.id === qid ? { ...q, ...patch } : q)));
  };

  const removeQuestion = (qid) => {
    // Rimuovi puntatori orfani sia da `next_question_id` (parent → child)
    // sia da `parent_question_id/parent_answer_id` (child → parent), per
    // evitare figli fluttuanti che non compaiono più nel wizard.
    const cleaned = questions
      .filter(q => q.id !== qid)
      .map(q => ({
        ...q,
        answers: (q.answers || []).map(a =>
          a.next_question_id === qid ? { ...a, next_question_id: null } : a,
        ),
        parent_question_id: q.parent_question_id === qid ? null : q.parent_question_id,
        parent_answer_id: q.parent_question_id === qid ? null : q.parent_answer_id,
      }));
    if (cleaned.length > 0 && !cleaned.some(q => q.is_root)) {
      cleaned[0] = { ...cleaned[0], is_root: true };
    }
    onChange(cleaned);
  };

  const setRoot = (qid) => {
    onChange(questions.map(q => ({ ...q, is_root: q.id === qid })));
  };

  const patchAnswer = (qid, aid, patch) => {
    onChange(questions.map(q => {
      if (q.id !== qid) return q;
      return {
        ...q,
        answers: (q.answers || []).map(a => (a.id === aid ? { ...a, ...patch } : a)),
      };
    }));
  };

  const addAnswer = (qid) => {
    onChange(questions.map(q => {
      if (q.id !== qid) return q;
      return {
        ...q,
        answers: [
          ...(q.answers || []),
          { id: genId("a"), label: "", price_modifier: { type: "pct", value: 0 }, next_question_id: null },
        ],
      };
    }));
  };

  const removeAnswer = (qid, aid) => {
    onChange(questions.map(q => {
      if (q.id !== qid) return q;
      return { ...q, answers: (q.answers || []).filter(a => a.id !== aid) };
    }));
  };

  const cloneFromService = () => {
    const src = servicesForClone.find(s => s._id === cloneFromId);
    if (!src || !Array.isArray(src.pricing_logic) || src.pricing_logic.length === 0) {
      return;
    }
    // Rigenera tutti gli ID per evitare collisioni; rimappa next_question_id.
    const idMap = {};
    for (const q of src.pricing_logic) {
      idMap[q.id] = genId("q");
      for (const a of (q.answers || [])) {
        idMap[a.id] = genId("a");
      }
    }
    const cloned = src.pricing_logic.map((q, idx) => ({
      id: idMap[q.id],
      question: q.question || "",
      is_root: !!q.is_root,
      order: typeof q.order === "number" ? q.order : idx,
      parent_question_id: q.parent_question_id ? (idMap[q.parent_question_id] || null) : null,
      parent_answer_id: q.parent_answer_id ? (idMap[q.parent_answer_id] || null) : null,
      answers: (q.answers || []).map(a => ({
        id: idMap[a.id],
        label: a.label || "",
        price_modifier: { ...(a.price_modifier || { type: "pct", value: 0 }) },
        next_question_id: a.next_question_id ? (idMap[a.next_question_id] || null) : null,
      })),
    }));
    onChange(cloned);
    setCloneFromId("");
  };

  const clonableServices = useMemo(
    () => (servicesForClone || []).filter(
      s => s._id !== currentServiceId
        && s.has_flexible_price
        && Array.isArray(s.pricing_logic)
        && s.pricing_logic.length > 0,
    ),
    [servicesForClone, currentServiceId],
  );

  const previewSelected = useMemo(
    () => ({ has_flexible_price: true, pricing_logic: questions, base_price: Number(previewBasePrice) || 0 }),
    [questions, previewBasePrice],
  );

  return (
    <div className="space-y-3" data-testid="pricing-logic-editor">
      {/* Iter118: Toolbar duplica + preview */}
      <div className="flex flex-wrap items-end gap-2 p-2 rounded-md bg-slate-50 border border-slate-200">
        {clonableServices.length > 0 && (
          <div className="flex items-end gap-1" data-testid="pricing-clone-tools">
            <div className="flex flex-col">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">
                Duplica da servizio
              </label>
              <select
                value={cloneFromId}
                onChange={(e) => setCloneFromId(e.target.value)}
                className="h-8 border rounded px-2 text-xs bg-white min-w-[180px]"
                data-testid="pricing-clone-select"
              >
                <option value="">— seleziona —</option>
                {clonableServices.map(s => (
                  <option key={s._id} value={s._id}>
                    {s.name} ({(s.pricing_logic || []).length} Q)
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!cloneFromId}
              onClick={cloneFromService}
              data-testid="pricing-clone-btn"
            >
              <Copy size={12} className="mr-1" /> Duplica
            </Button>
          </div>
        )}
        <div className="flex-1" />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setPreviewOpen(v => !v)}
          disabled={questions.length === 0}
          data-testid="pricing-preview-toggle"
        >
          {previewOpen ? <EyeSlash size={12} className="mr-1" /> : <Eye size={12} className="mr-1" />}
          {previewOpen ? "Chiudi anteprima" : "Anteprima wizard"}
        </Button>
      </div>

      {previewOpen && questions.length > 0 && (
        <div className="p-3 rounded-lg border-2 border-emerald-300 bg-emerald-50/30" data-testid="pricing-live-preview">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <label className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">
              Prezzo base di prova
            </label>
            <input
              type="number"
              step="1"
              value={previewBasePrice}
              onChange={(e) => setPreviewBasePrice(e.target.value)}
              className="h-8 w-24 border rounded px-2 text-sm bg-white"
              data-testid="pricing-preview-base-price"
            />
            <span className="text-xs text-slate-600">€</span>
            <div className="flex-1" />
            <div className="text-xs text-slate-700">
              Delta:{" "}
              <span className={previewDelta > 0 ? "font-semibold text-red-700" : previewDelta < 0 ? "font-semibold text-emerald-700" : "font-semibold text-slate-500"}>
                {previewDelta > 0 ? "+" : ""}{previewDelta.toFixed(2)} €
              </span>
            </div>
            <div className="text-xs font-bold text-emerald-900" data-testid="pricing-preview-total">
              Totale: {((Number(previewBasePrice) || 0) + previewDelta).toFixed(2)} €
            </div>
            <button
              type="button"
              onClick={() => setPreviewAnswers([])}
              className="text-[11px] text-emerald-700 hover:underline"
              data-testid="pricing-preview-reset"
            >
              Reset
            </button>
          </div>
          <FlexiblePriceWizard
            pricingLogic={questions}
            basePrice={Number(previewBasePrice) || 0}
            pricingAnswers={previewAnswers}
            setPricingAnswers={setPreviewAnswers}
            onDeltaChange={setPreviewDelta}
          />
        </div>
      )}
      {questions.length === 0 && (
        <div className="text-[11px] text-slate-500 italic p-3 border border-dashed border-slate-300 rounded-md">
          Nessuna domanda configurata. Aggiungi la prima domanda per iniziare a costruire il flusso.
        </div>
      )}

      {/* Iter122: pulsante anteprima albero verticale */}
      {questions.length > 0 && (
        <div className="flex justify-end mb-1">
          <button
            type="button"
            onClick={() => setTreeOpen(v => !v)}
            className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            data-testid="pricing-tree-toggle"
          >
            <TreeStructure size={12} />
            {treeOpen ? "Nascondi albero" : "Mostra albero"}
          </button>
        </div>
      )}
      {treeOpen && <PricingTreeView questions={questions} />}

      {/* Iter122: DnD context per riordinare le domande */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext
          items={[...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(q => q.id)}
          strategy={verticalListSortingStrategy}
        >
      {/* Iter121: ordina per `order` prima del rendering */}
      {[...questions]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((q, sortedIdx, sortedArr) => (
          <SortableQuestionCard
            key={q.id}
            q={q}
            sortedIdx={sortedIdx}
            sortedArr={sortedArr}
            questions={questions}
            patchQuestion={patchQuestion}
            setRoot={setRoot}
            moveQuestion={moveQuestion}
            setParent={setParent}
            removeQuestion={removeQuestion}
            patchAnswer={patchAnswer}
            addAnswer={addAnswer}
            removeAnswer={removeAnswer}
          />
        ))}
        </SortableContext>
      </DndContext>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addQuestion}
        data-testid="pricing-add-question"
      >
        <Plus size={14} className="mr-1" /> Aggiungi domanda
      </Button>
    </div>
  );
}

export default PricingLogicEditor;

// ==================== Iter122: Sortable Card + Tree View ====================

function SortableQuestionCard({
  q, sortedIdx, sortedArr, questions,
  patchQuestion, setRoot, moveQuestion, setParent, removeQuestion,
  patchAnswer, addAnswer, removeAnswer,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: q.id });
  const idx = questions.findIndex(x => x.id === q.id);
  const depthOf = (qid, visited = new Set()) => {
    const qq = questions.find(x => x.id === qid);
    if (!qq || !qq.parent_question_id || visited.has(qid)) return 0;
    visited.add(qid);
    return 1 + depthOf(qq.parent_question_id, visited);
  };
  const depth = depthOf(q.id);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginLeft: `${Math.min(depth, 4) * 20}px`,
    opacity: isDragging ? 0.5 : 1,
  };
  const nextOptions = questions.filter(qq => qq.id !== q.id);

  return (
    <div
      ref={setNodeRef}
      className={`p-3 rounded-lg border-2 mb-2 ${q.is_root ? "border-indigo-400 bg-indigo-50/40" : "border-slate-200 bg-white"} ${isDragging ? "shadow-lg" : ""}`}
      style={style}
      data-testid={`pricing-question-${idx}`}
      data-depth={depth}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="text-slate-400 hover:text-slate-700 cursor-grab active:cursor-grabbing p-0.5 shrink-0"
            data-testid={`pricing-drag-handle-${idx}`}
            aria-label="Trascina per riordinare"
            title="Trascina per riordinare"
          >
            <DotsSixVertical size={16} weight="bold" />
          </button>
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold shrink-0">
            Domanda {sortedIdx + 1}
          </span>
          {q.is_root ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-semibold">
              <Star size={10} weight="fill" /> INIZIALE
            </span>
          ) : (
            <button
              type="button"
              className="text-[10px] text-indigo-600 hover:underline"
              onClick={() => setRoot(q.id)}
              data-testid={`pricing-set-root-${idx}`}
            >
              Imposta come iniziale
            </button>
          )}
          {depth > 0 && (
            <span className="text-[10px] text-slate-500 italic">↳ figlia di livello {depth}</span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <label
            className="flex items-center gap-1 text-[10px] font-semibold text-slate-600 cursor-pointer select-none pr-2"
            title="Il cliente potrà selezionare più risposte. I maggiorazioni verranno sommati (€ + €, % + %)."
            data-testid={`pricing-toggle-multi-${idx}`}
          >
            <input
              type="checkbox"
              checked={!!q.allow_multiple_answers}
              onChange={(e) => patchQuestion(q.id, { allow_multiple_answers: e.target.checked })}
              className="h-3 w-3 accent-indigo-600"
              data-testid={`pricing-toggle-multi-input-${idx}`}
            />
            Selezione multipla
          </label>
          <button type="button" onClick={() => moveQuestion(q.id, "up")} disabled={sortedIdx === 0}
            className="text-slate-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed p-1"
            data-testid={`pricing-move-up-${idx}`} aria-label="Sposta su">
            <ArrowUp size={12} />
          </button>
          <button type="button" onClick={() => moveQuestion(q.id, "down")} disabled={sortedIdx === sortedArr.length - 1}
            className="text-slate-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed p-1"
            data-testid={`pricing-move-down-${idx}`} aria-label="Sposta giù">
            <ArrowDown size={12} />
          </button>
          <button type="button" onClick={() => removeQuestion(q.id)}
            className="text-red-600 hover:text-red-800 p-1"
            data-testid={`pricing-remove-question-${idx}`} aria-label="Rimuovi domanda">
            <Trash size={14} />
          </button>
        </div>
      </div>

      {/* Iter128: warning visivo per domande senza testo — l'utente ha
          segnalato la q `q_1785850925735_7081` con `question=""`. Bordo
          rosso + messaggio inline così il problema è evidente in admin. */}
      {/* Iter162: Textarea multi-riga senza limite di caratteri per supportare
          testi lunghi (dichiarazioni legali, clausole, note esplicative). */}
      <Textarea
        value={q.question || ""}
        onChange={(e) => patchQuestion(q.id, { question: e.target.value })}
        rows={3}
        placeholder="Es. L'immobile è superiore a 100 mq?"
        className={
          "mb-1 text-sm resize-y " +
          (!(q.question || "").trim() ? "border-red-400 ring-1 ring-red-200 bg-red-50/60" : "")
        }
        data-testid={`pricing-question-text-${idx}`}
      />
      {!(q.question || "").trim() && (
        <div
          className="mb-3 text-[11px] text-red-700 flex items-center gap-1"
          data-testid={`pricing-question-empty-warn-${idx}`}
        >
          ⚠️ Testo della domanda vuoto — il cliente non capirebbe cosa scegliere. Inserisci il testo prima di salvare.
        </div>
      )}
      {q.allow_multiple_answers && (
        <div
          className="mb-3 -mt-1 text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1.5"
          data-testid={`pricing-multi-note-${idx}`}
        >
          Il cliente potrà selezionare più risposte. I maggiorazioni verranno sommati (€ + €, % + %).
        </div>
      )}

      {!q.is_root && (
        <div className="mb-3 p-2 rounded bg-amber-50/60 border border-amber-200" data-testid={`pricing-conditional-${idx}`}>
          <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold mb-1">
            🔀 Mostra questa domanda solo se…
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={q.parent_question_id || ""}
              onChange={(e) => setParent(q.id, e.target.value || null, null)}
              className="h-8 border rounded px-1 text-xs bg-white"
              data-testid={`pricing-parent-question-${idx}`}
            >
              <option value="">— nessuna condizione —</option>
              {questions.filter(qq => qq.id !== q.id).map(qq => {
                const qqIdx = [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).findIndex(x => x.id === qq.id);
                return (
                  <option key={qq.id} value={qq.id}>
                    Q{qqIdx + 1}: {(qq.question || "senza testo").slice(0, 40)}
                  </option>
                );
              })}
            </select>
            <select
              value={q.parent_answer_id || ""}
              onChange={(e) => setParent(q.id, q.parent_question_id, e.target.value || null)}
              disabled={!q.parent_question_id}
              className="h-8 border rounded px-1 text-xs bg-white disabled:bg-slate-100 disabled:text-slate-400"
              data-testid={`pricing-parent-answer-${idx}`}
            >
              <option value="">— risposta —</option>
              {q.parent_question_id && (questions.find(x => x.id === q.parent_question_id)?.answers || []).map(a => (
                <option key={a.id} value={a.id}>= {a.label || "senza etichetta"}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
          Risposte possibili
        </div>
        {(q.answers || []).map((a, aidx) => (
          <div key={a.id}
            className="grid grid-cols-12 gap-2 items-center p-2 rounded bg-slate-50 border border-slate-200"
            data-testid={`pricing-answer-${idx}-${aidx}`}>
            <Input
              value={a.label || ""}
              onChange={(e) => patchAnswer(q.id, a.id, { label: e.target.value.slice(0, 100) })}
              placeholder="Es. Sì / No"
              className="col-span-4 text-sm"
              data-testid={`pricing-answer-label-${idx}-${aidx}`}
            />
            <input type="number" step="0.5"
              value={a.price_modifier?.value ?? 0}
              onChange={(e) => patchAnswer(q.id, a.id, {
                price_modifier: { ...(a.price_modifier || {}), value: e.target.value },
              })}
              className="col-span-2 h-9 border rounded px-2 text-sm text-right"
              data-testid={`pricing-answer-value-${idx}-${aidx}`}
            />
            <select
              value={a.price_modifier?.type || "pct"}
              onChange={(e) => patchAnswer(q.id, a.id, {
                price_modifier: { ...(a.price_modifier || {}), type: e.target.value },
              })}
              className="col-span-1 h-9 border rounded px-1 text-sm"
              data-testid={`pricing-answer-type-${idx}-${aidx}`}
            >
              <option value="pct">%</option>
              <option value="eur">€</option>
            </select>
            <div className="col-span-4 flex items-center gap-1 text-xs text-slate-600">
              <ArrowRight size={12} />
              <select
                value={a.next_question_id || ""}
                onChange={(e) => patchAnswer(q.id, a.id, { next_question_id: e.target.value || null })}
                className="flex-1 h-9 border rounded px-1 text-xs bg-white"
                data-testid={`pricing-answer-next-${idx}-${aidx}`}
              >
                <option value="">Fine flusso</option>
                {nextOptions.map(qq => (
                  <option key={qq.id} value={qq.id}>
                    {qq.question ? qq.question.slice(0, 30) : "senza testo"}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" onClick={() => removeAnswer(q.id, a.id)}
              disabled={(q.answers || []).length <= 1}
              className="col-span-1 text-red-600 hover:text-red-800 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid={`pricing-remove-answer-${idx}-${aidx}`}
              aria-label="Rimuovi risposta">
              <Trash size={14} />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => addAnswer(q.id)}
          className="text-xs text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
          data-testid={`pricing-add-answer-${idx}`}>
          <Plus size={12} /> Aggiungi risposta
        </button>
      </div>
    </div>
  );
}

// Iter122: anteprima albero verticale con nodi indentati e linee tratteggiate
// che collegano padre → figlio. Mostra sopra a ogni figlio l'etichetta della
// risposta che scatena la visualizzazione.
function PricingTreeView({ questions }) {
  const sorted = [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const roots = sorted.filter(q => !q.parent_question_id);

  const renderNode = (q, level = 0) => {
    const children = sorted.filter(x => x.parent_question_id === q.id);
    return (
      <div key={q.id} className="relative">
        <div
          className={`inline-block px-3 py-1.5 rounded-md text-xs font-medium border-2 mb-1 ${
            q.is_root
              ? "bg-indigo-600 text-white border-indigo-700"
              : "bg-white text-slate-800 border-slate-300"
          }`}
          data-testid={`tree-node-${q.id}`}
        >
          {q.question || "(senza testo)"}
        </div>
        {children.length > 0 && (
          <div className="ml-6 pl-4 border-l-2 border-dashed border-slate-300 mt-1">
            {children.map(c => {
              const parentAns = (q.answers || []).find(a => a.id === c.parent_answer_id);
              return (
                <div key={c.id} className="mb-1">
                  {parentAns && (
                    <div className="text-[10px] text-amber-700 italic mb-0.5">
                      ↳ se risposta = "{parentAns.label || "?"}"
                    </div>
                  )}
                  {renderNode(c, level + 1)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (sorted.length === 0) return null;

  return (
    <div className="p-4 rounded-lg border-2 border-slate-200 bg-slate-50/60 mb-3" data-testid="pricing-tree-view">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
        🌳 Diagramma dell'albero delle domande
      </div>
      <div className="space-y-2">
        {roots.map(r => renderNode(r))}
      </div>
    </div>
  );
}
